import { Injectable } from '@angular/core';
import {
  Address,
  AddressUtil,
  callWithRetry,
  TokenHelper__factory,
} from '@31third/common';
import { AbiCoder } from 'ethers';
import {
  ChainSettingsState,
  RebalancingRepository,
  SettingsRepository,
} from '../repository';
import {
  Allowance,
  ChainRepository,
  MAX_WEI,
  SignerService,
  Token,
  Trade,
  Transaction,
} from 'common';
import { RebalancingStep } from '../component';
import { Erc20Service } from './erc20.service';

@Injectable({
  providedIn: 'root',
})
export class AllowanceService {
  private static readonly ALLOWANCE_TYPE_RESET = 'RESET';

  constructor(
    private erc20Service: Erc20Service,
    private signerService: SignerService,
    private rebalancingRepository: RebalancingRepository,
    private chainRepository: ChainRepository,
    private settingsRepository: SettingsRepository,
  ) {}

  private getActiveChainSetting(): ChainSettingsState | undefined {
    return this.settingsRepository.getActiveChainSetting();
  }

  public async getAllowance(
    token: Token,
    owner: Address,
    spender: Address,
  ): Promise<bigint> {
    const contract = await this.erc20Service.getContractForAddress(
      token.address,
    );
    try {
      return callWithRetry(3, 1000, contract, 'allowance', owner, spender);
    } catch (e) {
      return Promise.resolve(0n);
    }
  }

  public async getAllowances(
    tokenHelperAddress: Address,
    addresses: Address[],
    owner: Address,
    spender: Address,
  ): Promise<bigint[]> {
    const contract = TokenHelper__factory.connect(
      tokenHelperAddress,
      this.signerService.getSigner(),
    );
    return callWithRetry(
      3,
      1000,
      contract,
      'tokenAllowances',
      owner,
      spender,
      addresses,
    );
  }

  public async approveAllowance(
    allowance: Allowance,
    approveMax: boolean,
  ): Promise<Transaction> {
    let value = MAX_WEI;
    if (!approveMax) {
      value = allowance.resetNeeded ? 0n : allowance.allowanceNeeded;
    }

    return this.approveAndHandleTransaction(
      allowance.token,
      allowance.target,
      value,
    );
  }

  private async approveAndHandleTransaction(
    token: Token,
    target: Address,
    value = MAX_WEI,
  ): Promise<Transaction> {
    const transaction = await this.erc20Service.approve(token, target, value);
    transaction.getFinishedObservable().subscribe((finished: boolean) => {
      if (finished && transaction.success) {
        this.updateAllowancesNeeded(transaction);
      }
    });
    return Promise.resolve(transaction);
  }

  public async updateAllowancesNeeded(
    transaction?: Transaction,
  ): Promise<void> {
    const storeValue = this.rebalancingRepository.store.getValue();
    if (
      storeValue.step >= RebalancingStep.APPROVAL_REQUIRED &&
      storeValue.step <= RebalancingStep.APPROVAL_OK &&
      storeValue.rebalancing &&
      storeValue.walletAddress
    ) {
      const blockchainAllowances = await this.getBlockchainAllowances(
        storeValue.rebalancing.trades,
        transaction,
      );
      let i = 0;
      for (const trade of storeValue.rebalancing.trades) {
        const localAllowance = this.getLocalAllowance(trade);

        if (localAllowance) {
          if (localAllowance.resetNeeded) {
            this.rebalancingRepository.updateAllowance(localAllowance.index, {
              resetNeeded: blockchainAllowances[i] !== 0n,
            });
          } else {
            if (
              localAllowance.token.allowanceType ===
                AllowanceService.ALLOWANCE_TYPE_RESET &&
              blockchainAllowances[i] < localAllowance.allowanceNeeded &&
              blockchainAllowances[i] > 0n
            ) {
              this.rebalancingRepository.updateAllowance(localAllowance.index, {
                resetNeeded: true,
              });
            } else {
              this.rebalancingRepository.updateAllowance(localAllowance.index, {
                done: blockchainAllowances[i] >= localAllowance.allowanceNeeded,
              });
            }
          }
        }
        i++;
      }
    }
  }

  private getLocalAllowance(trade: Trade): Allowance | undefined {
    const rebalancing = this.rebalancingRepository.store.getValue().rebalancing;
    const allowances =
      this.rebalancingRepository.store.getValue().rebalancing?.allowances;
    if (rebalancing && allowances) {
      for (const allowance of allowances) {
        if (
          (this.getActiveChainSetting()?.batchTrade ||
            allowance.target === trade.allowanceTarget) &&
          AddressUtil.equals(trade.from.address, allowance.token.address)
        ) {
          return allowance;
        }
      }
    }
    return undefined;
  }

  private async getBlockchainAllowances(
    trades: Trade[],
    transaction?: Transaction,
  ): Promise<bigint[]> {
    const allowances: bigint[] = [];
    try {
      const storeValue = this.rebalancingRepository.store.getValue();
      const tokenHelperAddress =
        this.chainRepository.store.getValue().chain?.tokenHelperAddress;
      if (tokenHelperAddress) {
        const allowanceTargets: Address[] = [];
        for (const trade of trades) {
          if (allowanceTargets.includes(trade.allowanceTarget)) {
            continue;
          }
          allowanceTargets.push(trade.allowanceTarget);
          // filter trades by allowanceTarget
          const filteredTrades = trades.filter(
            filteredTrade =>
              filteredTrade.allowanceTarget === trade.allowanceTarget,
          );
          if (storeValue.rebalancing && storeValue.walletAddress) {
            const allowancesResult = await this.getAllowances(
              tokenHelperAddress,
              filteredTrades.map(trade => trade.from.address),
              storeValue.walletAddress!,
              this.getActiveChainSetting()?.batchTrade
                ? storeValue.rebalancing.txHandler!
                : trade.txHandler,
            );
            filteredTrades.forEach((trade, index) => {
              allowances[trades.indexOf(trade)] = allowancesResult[index];
            });
          }
        }
      } else {
        for (const trade of trades) {
          if (
            storeValue.rebalancing &&
            storeValue.walletAddress &&
            ((this.getActiveChainSetting()?.batchTrade &&
              storeValue.rebalancing.txHandler) ||
              trade.txHandler)
          ) {
            allowances.push(
              await this.getAllowance(
                trade.from,
                storeValue.walletAddress,
                this.getActiveChainSetting()?.batchTrade
                  ? storeValue.rebalancing.txHandler!
                  : trade.txHandler,
              ),
            );
          }
        }
      }
    } catch (error) {
      // Try to decode from transaction data if present
      trades.forEach((trade, index) => {
        if (
          transaction &&
          AddressUtil.equals(trade.from.address, transaction.to)
        ) {
          try {
            const [, decodedAmount] = AbiCoder.defaultAbiCoder().decode(
              ['address', 'uint256'],
              transaction.getDataWithoutSelector(),
            );
            allowances[index] = decodedAmount;
          } catch (e) {
            // empty on purpose
          }
        }
      });
    }
    return allowances;
  }
}
